'use client';

import { Drawer, Group } from '@mantine/core';
import { useClickOutside, useDisclosure } from '@mantine/hooks';
import cn from 'classnames';
import { useMeCustomer } from 'medusa-react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useState, useContext, useMemo, useEffect, useCallback } from 'react';

import Button from '@/components/Button';
import LoginDropdown from '@/components/LoginDropdown';
import LoginForm from '@/components/LoginForm';
import RegisterForm from '@/components/RegisterForm';
import ScreenWidthContext from '@/contexts/ScreenWidthContext';
import ToastContext from '@/contexts/ToastContext';
import { useUserAgent } from '@/contexts/UserAgentContext';
import { View } from '@/utils/types/login';
import userIcon from 'public/icons/user-icon.svg';

import IconButton from '../IconButton';

import styles from './styles.module.scss';
import { Props } from './types';

const LoginSidebar = ({ classNames, onlyDrawer }: Props) => {
  const { isMobile } = useContext(ScreenWidthContext);
  const { isMacOS, isAndroid } = useUserAgent();
  const { openDialog } = useContext(ToastContext);
  const [opened, { open, close: closeModal }] = useDisclosure(false);
  const [currentView, setCurrentView] = useState<View>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { customer } = useMeCustomer();
  const ref = useClickOutside(closeModal);
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [redirectUrl, setRedirectUrl] = useState('');

  const cleanParams = useCallback(() => {
    if (pathname) {
      router.replace(pathname);
    }
  }, [pathname, router]);

  const handleClose = useCallback(() => {
    cleanParams();
    closeModal();
    setRedirectUrl('');
  }, [cleanParams, closeModal]);

  const [isLoggedIn, setIsLoggedIn] = useState(!!customer);

  const handleLoggedIn = (loggedIn: boolean) => {
    setIsLoggedIn(loggedIn);
  };

  const toggleView = useCallback(() => {
    setCurrentView(currentView === 'login' ? 'register' : 'login');
  }, [currentView]);

  const openView = useCallback((type: View) => {
    setCurrentView(type);
  }, []);

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const handleMouseOver = () => {
    if (isMobile) return;
    setIsDropdownOpen(true);
  };

  const handleMouseOut = () => {
    if (isMobile) return;
    setIsDropdownOpen(false);
  };

  const openRelevantView = useCallback(
    (type: View) => {
      open();
      openView(type);
    },
    [open, openView],
  );

  const viewTitle = useMemo(() => {
    if (currentView === 'login') {
      return <Drawer.Title className={styles.title}>Zaloguj się</Drawer.Title>;
    }

    if (currentView === 'register') {
      return (
        <Drawer.Title className={styles.title}>Zarejestruj się</Drawer.Title>
      );
    }
  }, [currentView]);

  const handleLoginClick = useCallback(() => {
    handleClose();

    if (redirectUrl) {
      router.push(redirectUrl);
    }

    openDialog('Zostałeś pomyślnie zalogowany.');
  }, [handleClose, openDialog, redirectUrl, router]);

  const handleRegisterClick = useCallback(() => {
    handleClose();

    if (redirectUrl) {
      router.push(redirectUrl);
    }

    openDialog('Twoje konto zostało utworzone. Zostałeś pomyślnie zalogowany.');
  }, [handleClose, openDialog, redirectUrl, router]);

  useEffect(() => {
    const redirect = searchParams?.get('redirect-url');
    const view = searchParams?.get('view');

    if (redirect && view) {
      setRedirectUrl('/' + redirect);
      openRelevantView(view as View);
    }
  }, [openRelevantView, searchParams]);

  const viewContent = useMemo(() => {
    if (currentView === 'login') {
      return (
        <div id="logging">
          <LoginForm
            openToast={handleLoginClick}
            onLoggedIn={handleLoggedIn}
            close={close}
            redirectUrl={redirectUrl}
          />
          <div className={styles.registration}>
            <h3
              className={cn(styles.label, {
                [styles.avenirFont]: isMacOS || isAndroid,
              })}
            >
              Nie masz jeszcze konta?
            </h3>
            <Button
              variant="primary"
              isOutline
              isCapitalize
              isBiggerPadding
              fullWidth
              onClick={toggleView}
            >
              Zarejestruj się
            </Button>
          </div>
          <div className={styles.vendorLogging}>
            <h3
              className={cn(styles.label, {
                [styles.avenirFont]: isMacOS || isAndroid,
              })}
            >
              Jesteś sprzedawcą?
            </h3>
            <div className={styles.logging}>
              <Link href={`${process.env.NEXT_PUBLIC_LOGIN_VENDOR_URL}`}>
                Zaloguj się
              </Link>
              <p>&nbsp;na konto sprzedawcy</p>
            </div>
          </div>
        </div>
      );
    }

    if (currentView === 'register') {
      return (
        <div id="registration">
          <RegisterForm
            openToast={handleRegisterClick}
            redirectUrl={redirectUrl}
          />
          <div className={styles.registration}>
            <h3
              className={cn(styles.label, {
                [styles.avenirFont]: isMacOS || isAndroid,
              })}
            >
              Masz już konto?
            </h3>
            <Button
              variant="primary"
              isOutline
              isCapitalize
              isBiggerPadding
              fullWidth
              onClick={toggleView}
            >
              Zaloguj
            </Button>
          </div>
          <div className={styles.vendorLogging}>
            <h3
              className={cn(styles.label, {
                [styles.avenirFont]: isMacOS || isAndroid,
              })}
            >
              Chcesz sprzedawać na Patynie?
            </h3>
            <div className={styles.logging}>
              <Link href={`${process.env.NEXT_PUBLIC_REGISTER_VENDOR_URL}`}>
                Załóż konto sprzedawcy
              </Link>
              <p>&nbsp;i zacznij sprzedawać!</p>
            </div>
          </div>
        </div>
      );
    }
  }, [
    currentView,
    handleLoginClick,
    handleRegisterClick,
    isAndroid,
    isMacOS,
    redirectUrl,
    toggleView,
  ]);

  return (
    <div>
      <Drawer.Root
        position="right"
        opened={opened}
        onClose={handleClose}
        size={768}
      >
        <Drawer.Overlay />
        <Drawer.Content className={styles.content}>
          <Drawer.Header className={styles.header}>
            {viewTitle}
            <Drawer.CloseButton className={styles.closeButton} />
          </Drawer.Header>
          {viewContent}
        </Drawer.Content>
      </Drawer.Root>
      {!onlyDrawer && (
        <>
          {!isMobile ? (
            <>
              {customer?.id || isLoggedIn ? (
                <Group position="center" className={styles.dropdownWrapper}>
                  <IconButton href={'/moje-konto'}>
                    <>
                      <Image
                        src={userIcon}
                        alt="Ikonka użytkownika"
                        height={20}
                        width={20}
                      />
                      <p>Moje konto</p>
                    </>
                  </IconButton>
                </Group>
              ) : (
                <Group
                  position="center"
                  className={styles.dropdownWrapper}
                  onMouseOver={handleMouseOver}
                  onMouseOut={handleMouseOut}
                >
                  <IconButton onClick={toggleDropdown}>
                    <Image
                      src={userIcon}
                      alt="Ikonka użytkownika"
                      height={20}
                      width={20}
                    />
                    <p>Zaloguj</p>
                  </IconButton>
                  {isDropdownOpen && (
                    <LoginDropdown
                      onClick={openRelevantView}
                      classNames={classNames}
                    />
                  )}
                </Group>
              )}
            </>
          ) : (
            <>
              {customer?.id || isLoggedIn ? (
                <Group
                  position="center"
                  className={styles.dropdownWrapper}
                  onClick={toggleDropdown}
                >
                  <IconButton href={'/moje-konto'}>
                    <Image
                      src={userIcon}
                      alt="Ikonka użytkownika"
                      height={20}
                      width={20}
                    />
                    <p>Moje konto</p>
                  </IconButton>
                </Group>
              ) : (
                <Group
                  position="center"
                  className={styles.dropdownWrapper}
                  onClick={toggleDropdown}
                  ref={isMobile ? null : ref}
                  onMouseOver={handleMouseOver}
                  onMouseOut={handleMouseOut}
                >
                  <IconButton>
                    <Image
                      src={userIcon}
                      alt="Ikonka użytkownika"
                      height={20}
                      width={20}
                    />
                    <p>Zaloguj</p>
                  </IconButton>
                  {isDropdownOpen && (
                    <LoginDropdown
                      onClick={openRelevantView}
                      classNames={classNames}
                    />
                  )}
                </Group>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default LoginSidebar;
